import { createFileRoute, notFound } from '@tanstack/react-router';

import { useBesluitId } from '@/api/queries/besluit';
import { useHoofdstukId } from '@/api/queries/objects';
import { useOverwegingByHoofdstukLineageId } from '@/api/queries/overwegingHoofdstuk';

import { BesluitChapterNavigation } from '../-components/BesluitChapterNavigation';
import { HTMLContent } from '../-components/HTMLContent';
import {
  ensureQueryDataViewerBesluitHoofdstukken,
  useViewerBesluitHoofdstukken,
} from './-utils/useViewerBesluitHoofdstukken';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout/inhoudelijke-overwegingen/$hoofdstukId'
)({
  loader: async ({
    params: { besluitId, hoofdstukId },
    context: { queryClient },
  }) => {
    const hoofdstuk = await queryClient.ensureQueryData(
      useHoofdstukId.getOptions(hoofdstukId)
    );

    if (!hoofdstuk) throw notFound();

    await Promise.all([
      queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
      queryClient.ensureQueryData(
        useOverwegingByHoofdstukLineageId.queryOptions({
          hoofdstukLineageId: hoofdstuk.Lineage,
          besluitId,
        })
      ),
      ensureQueryDataViewerBesluitHoofdstukken({ besluitId, queryClient }),
    ]);
  },
  component: InhoudelijkeOverwegingen,
});

/**
 * Route component
 */
function InhoudelijkeOverwegingen() {
  const { besluitId, hoofdstukId } = Route.useParams();

  const hoofdstuk = useHoofdstukId(hoofdstukId);
  const besluit = useBesluitId(besluitId, { staleTime: Infinity });

  const { grondslagen } = useViewerBesluitHoofdstukken({
    besluitId,
  });

  const overweging = useOverwegingByHoofdstukLineageId(
    { hoofdstukLineageId: hoofdstuk.data?.Lineage ?? '', besluitId },
    { enabled: !!hoofdstuk.data }
  );

  if (!besluit.data || hoofdstuk.isLoading || overweging.isLoading) return null;

  return (
    <>
      <HTMLContent
        besluit={besluit.data}
        title={
          <span
            dangerouslySetInnerHTML={{ __html: hoofdstuk.data?.Titel ?? '' }}
          />
        }
        content={overweging.data?.InhoudelijkeOverweging}
      />

      <BesluitChapterNavigation
        groupedGrondslag={grondslagen}
        hoofdstukId={hoofdstukId}
      />
    </>
  );
}
